import { SyntheticEvent, useState, FC, memo, useEffect } from 'react';
import classNames from 'classnames';

import MarketingAgreement from 'application/components/MarketingAgreement';
import { FORGOT_PAGE_PATH } from 'application/pages/EmailSendPage';
import { Link } from 'application/providers/RouterProvider';
import AnalyticsService, {
  EventNameAMP,
  ParamNameAMP,
  SignUpSourceValues,
} from 'application/services/AnalyticsService';
import SBBIDButton from 'application/components/SBBIDButton';

import { Country, OrganizationType } from 'domain/api/graphql/generated';

import Input, { InputType } from '@zvuk-b2b/react-uikit/ui/Input';
import PasswordInput from '@zvuk-b2b/react-uikit/ui/PasswordInput';
import PhoneInput from '@zvuk-b2b/react-uikit/ui/PhoneInput';
import Button from '@zvuk-b2b/react-uikit/ui/Button';
import Delimiter from '@zvuk-b2b/react-uikit/ui/Delimiter';
import Radio from '@zvuk-b2b/react-uikit/ui/Radio';

import { LegacyAuthFormProps } from './types';
import { isBelarusCountry } from './utils';
import { CookiePolicy } from './CookiePolicy';
import { TEXTS } from './texts';

import styles from './index.module.scss';

const PROD_BY_HOST_NAME = 'app.zvuk-b2b.by';

const LegacyAuthForm: FC<LegacyAuthFormProps> = ({
  isEmailDisabled = false,
  isPhoneAvailable = false,
  isRegisterMode = false,
  withSBBIDButton = false,
  isOrganizationTypeVisible = false,
  isSelfEmployedDisabled = false,
  organizationType,
  inputEmailLabel,
  inputEmailPlaceholder,
  inputPhoneLabel,
  inputPhonePlaceholder,
  inputPasswordLabel,
  inputPasswordPlaceholder,
  buttonContent,
  forgotLinkLabel,
  initData,
  className,
  isLoading,
  errors,
  onPhoneChange,
  onOrganizationTypeChange,
  onSubmit,
}) => {
  const [email, setEmail] = useState<string>(initData?.email || '');
  const [phone, setPhone] = useState<string>(initData?.phone || '');
  const [password, setPassword] = useState<string>('');
  const [marketingMaterialsAgreementReceived, setMarketingMaterialsAgreementReceived] = useState<boolean>(false);

  const isByCountry = isBelarusCountry(phone);

  const onChangePhone = (value: string) => {
    setPhone(value);
    onPhoneChange?.(value);
  };

  const onRegisterClick = (e: SyntheticEvent) => {
    e.preventDefault();

    if (isPhoneAvailable) {
      onSubmit({
        email: email.trim(),
        phone: phone.trim(),
        password,
        marketingMaterialsAgreementReceived,
      });
    } else {
      onSubmit({
        email: email.trim(),
        password,
        marketingMaterialsAgreementReceived,
      });
    }
  };

  useEffect(() => {
    AnalyticsService.event(EventNameAMP.EMAIL_SIGNUP_PAGE_SHOWN, {
      [ParamNameAMP.SIGN_UP_SOURCE]: SignUpSourceValues.EMAIL,
    });
  }, []);

  return (
    <form
      className={classNames(styles.form, className)}
      onSubmit={onRegisterClick}>
      <Input
        elementId="input_email"
        type={InputType.TEXT}
        value={email}
        error={errors?.email}
        isDisabled={isLoading || isEmailDisabled}
        containerClassName={styles.input}
        label={inputEmailLabel}
        placeholder={inputEmailPlaceholder}
        onChange={setEmail}
        onBlur={() => setEmail(email.trim())}
      />

      {isPhoneAvailable && (
        <PhoneInput
          elementId="input_tel"
          initialValue={phone}
          error={errors?.phone}
          isDisabled={isLoading}
          containerClassName={styles.input}
          label={inputPhoneLabel}
          placeholder={inputPhonePlaceholder}
          onChange={(value) => onChangePhone(value as string)}
          onBlur={() => setPhone(phone.trim())}
        />
      )}

      {isOrganizationTypeVisible && organizationType && onOrganizationTypeChange && (
        <div className={styles.organizationType}>
          <span className={styles.organizationTypeTitle}>{TEXTS.REGISTRATION_ORGANIZATION_TYPE_TITLE}</span>
          <div className={styles.organizationTypeOptions}>
            <Radio
              elementId="radio_email_registration_organization_legal"
              isChecked={organizationType === OrganizationType.Legal}
              onChange={() => onOrganizationTypeChange(OrganizationType.Legal)}>
              {TEXTS.REGISTRATION_ORGANIZATION_TYPE_LEGAL}
            </Radio>
            <Radio
              elementId="radio_email_registration_organization_self_employed"
              isChecked={organizationType === OrganizationType.SelfEmployed}
              isDisabled={isSelfEmployedDisabled}
              onChange={() => onOrganizationTypeChange(OrganizationType.SelfEmployed)}>
              {TEXTS.REGISTRATION_ORGANIZATION_TYPE_SELF_EMPLOYED}
            </Radio>
          </div>
        </div>
      )}

      <PasswordInput
        elementId="input_password"
        value={password}
        isDisabled={isLoading}
        error={errors?.password}
        containerClassName={styles.passwordInput}
        label={inputPasswordLabel}
        placeholder={inputPasswordPlaceholder}
        onChange={setPassword}
        onBlur={() => setPassword(password.trim())}
      />

      {forgotLinkLabel && (
        <Link
          className={styles.link}
          to={FORGOT_PAGE_PATH}>
          {forgotLinkLabel}
        </Link>
      )}

      <Button
        elementId="button_register"
        isLoading={isLoading}
        onClick={onRegisterClick}
        className={styles.button}>
        {buttonContent}
      </Button>

      {isRegisterMode && !isByCountry && (
        <MarketingAgreement
          marketingMaterialsAgreementReceived={marketingMaterialsAgreementReceived}
          setMarketingMaterialsAgreementReceived={setMarketingMaterialsAgreementReceived}
          isLoading={isLoading}
          className={styles.checkbox}
        />
      )}

      {isRegisterMode && window.location.hostname !== PROD_BY_HOST_NAME && withSBBIDButton && (
        <>
          <Delimiter
            className={styles.CodeInputFormDelimiter}
            title={TEXTS.DELIMITER_TEXT}
          />

          <SBBIDButton
            elementId="button_sbbid"
            key="button_sbbid"
            withModal={false}
            title={TEXTS.BUTTON_SBBID_TITLE}
          />
        </>
      )}

      {isByCountry && isRegisterMode && <CookiePolicy country={Country.By} />}

      <input
        type="submit"
        className={styles.hiddenInput}
      />
    </form>
  );
};

export default memo(LegacyAuthForm);
